import React from "react";
import { useAppStore } from "../store";
import { selectUser } from "../store/data/user";
import { EventPackage, EventType } from "../store/data/types";
import { useTeamConfig } from "./useTeamConfig";
import { CartManagerState } from "../store/data/purchaseSeats/cartManager";
import { PurchaseSeatCheckoutPayload } from "../api/cart";
import { globalConfig } from "../config/global_config";

const gtagEvents = {
  selectPackage: "select_package",
  selectEvents: "select_events",
  removeEvent: "remove_event",
  createdCheckout: "created_checkout",
  cancelledCart: "cancelled_cart",
  completedCheckout: "completed_checkout",
  conversion: "conversion",
};

export function useGoogleAnalytics() {
  const teamConfig = useTeamConfig();
  const user = useAppStore(selectUser);
  const { analyticsConfigs } = globalConfig;
  const { googleAnalyticsId: globalGoogleAnalyticsId } = analyticsConfigs;

  // package selection event
  function fireAnalyticsPkgSelect(pkg: EventPackage) {
    const gtag = window.gtag;
    const data = {
      teamSchema: teamConfig.teamSchema,
      userId: user?.userId,
      packageId: pkg?.id,
      packageTitle: pkg?.title,
      isBuildYourOwn: pkg?.isBuildYourOwn ? "yes" : "no",
      send_to: globalGoogleAnalyticsId,
    };
    gtag("event", gtagEvents.selectPackage, data);
  }

  // game selection event
  function fireAnalyticsEventSelect(selectedEvents: EventType[]) {
    const gtag = window.gtag;
    const data = {
      teamSchema: teamConfig.teamSchema,
      userId: user?.userId,
      eventCount: selectedEvents?.length,
      eventCodes: selectedEvents?.map((itm) => itm.eventCode).join(","),
      send_to: globalGoogleAnalyticsId,
    };
    gtag("event", gtagEvents.selectEvents, data);
  }

  // game remove event
  function fireAnalyticsEventRemove(removedEvent: EventType) {
    const gtag = window.gtag;
    const data = {
      teamSchema: teamConfig.teamSchema,
      userId: user?.userId,
      removedEventCode: removedEvent?.eventCode,
      send_to: globalGoogleAnalyticsId,
    };
    gtag("event", gtagEvents.removeEvent, data);
  }

  // checkout creation event
  function fireAnalyticsCheckoutCreated(cartState: CartManagerState) {
    const gtag = window.gtag;
    const data = {
      teamSchema: teamConfig.teamSchema,
      userId: user?.userId,
      cartId: cartState?.cartId,
      totalAmount: cartState?.allInPrice,
      totalSeats: cartState?.totalSeats,
      seatLocations: cartState?.cart?.map((itm) => itm.location).join(","),
      send_to: globalGoogleAnalyticsId,
    };
    gtag("event", gtagEvents.createdCheckout, data);
  }

  // cart cancel event
  function fireAnalyticsCancelledCart(cartState: CartManagerState) {
    const gtag = window.gtag;
    const data = {
      teamSchema: teamConfig.teamSchema,
      userId: user?.userId,
      cartId: cartState?.cartId,
      totalAmount: cartState?.allInPrice,
      totalSeats: cartState?.totalSeats,
      seatLocations: cartState?.cart?.map((itm) => itm.location).join(","),
      send_to: globalGoogleAnalyticsId,
    };
    gtag("event", gtagEvents.cancelledCart, data);
  }

  // checkout completed event
  function fireAnalyticsCompletedCheckout(
    checkoutData: PurchaseSeatCheckoutPayload
  ) {
    const gtag = window.gtag;
    const data = {
      teamSchema: teamConfig.teamSchema,
      userId: user?.userId,
      cartId: checkoutData?.cartId,
      cart: checkoutData.cart.map((itm) => itm.location).join(","),
      totalAmount: checkoutData?.allInPrice,
      totalSeats: checkoutData?.totalSeats,
      sessionId: checkoutData.sessionId,
      creditToUse: checkoutData.creditToUse,
      orderServiceFee: checkoutData.orderServiceFee,
      send_to: globalGoogleAnalyticsId,
    };
    gtag("event", gtagEvents.completedCheckout, data);
  }

  function fireTeamAnalyticsCompletedCheckout(
    checkoutData: PurchaseSeatCheckoutPayload,
    checkoutRes: any
  ) {
    if (!teamConfig.analyticsConfigs?.trackGoogleAnalyticsOnCheckout) return;
    const eventName = teamConfig?.analyticsConfigs?.customAnalyticsConversionEvent || gtagEvents.conversion; // prettier-ignore
    const gtag = window.gtag;
    const data = {
      send_to: teamConfig.analyticsConfigs.googleAnalyticsId,
      value: Number(checkoutData?.allInPrice || 0),
      currency: "USD",
      transaction_id: String(checkoutRes?.orderNumber),
    };
    gtag("event", eventName, data);
  }

  return {
    fireAnalyticsPkgSelect,
    fireAnalyticsEventSelect,
    fireAnalyticsEventRemove,
    fireAnalyticsCheckoutCreated,
    fireAnalyticsCancelledCart,
    fireAnalyticsCompletedCheckout,
    fireTeamAnalyticsCompletedCheckout,
  };
}
